import { useQuery, useQueryClient } from '@tanstack/react-query';

import Api from 'Api/Api';
import { url } from '@api-config';
import { IIngestionCounts } from '../../redux/ingestion.interface';
import { useEffect, useRef } from 'react';
import { fullIngestionStatusKeys, useFullIngestionStatus } from './useFullIngestionStatus.query';
import { useDSIngestionStatus } from './useDSIngestionStatus.query';

export const IngestionLogCountsQueryKey = ['ingestionLogCounts'];

type TProps = { isMenuOpen?: boolean };

export const useIngestionLogCounts = ({ isMenuOpen = false }: TProps) => {
  const { data: activeIngestions = [] } = useDSIngestionStatus();
  const { isRunning, isStopping } = useFullIngestionStatus();

  const query = useQuery<IIngestionCounts, Error>({
    queryKey: IngestionLogCountsQueryKey,
    queryFn: () => Api.get(url.statusLogCounts),
    staleTime: Infinity,

    // Every 30 seconds if the menu is open or there is an active ingestion
    refetchInterval:
      isMenuOpen || activeIngestions?.length > 0 || isStopping || isRunning ? 30000 : false,
  });

  const queryClient = useQueryClient();
  const previousDataRef = useRef<IIngestionCounts | null>(null);

  useEffect(() => {
    if (query.isSuccess) {
      const previousData = previousDataRef.current;
      const latestData = query.data;
      if (previousData && previousData.ingestionRunningCount !== latestData.ingestionRunningCount) {
        // Invalidate queries related to full ingestion status when counts change
        queryClient.invalidateQueries({ queryKey: fullIngestionStatusKeys.invalidationKey });
      }
      previousDataRef.current = latestData;
    }
  }, [query.isSuccess, query.data, queryClient]);

  return query;
};
